import { Button, Container, Group, Skeleton, Stack, Title, useMatches } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";

import { AuctionCard } from "./components";

import { Carousel } from "@/components/elements";
import { useAuctions } from "@/services/auctions/get-auctions";

import type { FC } from "react";

export type AuctionsCarouselProps = {
  getPath?: (id: string) => string;
  to?: string;
};

export const AuctionsCarousel: FC<AuctionsCarouselProps> = ({ getPath, to }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useAuctions({ pageSize: 10, status: "upcoming" });

  const slidesToRender = isLoading
    ? Array(1).fill(null)
    : [...(data?.results || [])].sort(
        (a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
      );
  const slidesCount = slidesToRender.length;
  const hasIndicators = useMatches({
    base: slidesCount > 2,
    sm: slidesCount > 2,
    md: slidesCount > 3,
  });

  function handleOnCardClick(auctionId?: string) {
    if (getPath && auctionId) {
      return () => {
        navigate(getPath(auctionId));
      };
    }
    return undefined;
  }

  return (
    <>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={4}>{t("components.Auctions.upcoming_auctions")}</Title>
          {to && (
            <Button component={Link} to={to} aria-label={t("components.Auctions.all_auctions")}>
              {t("components.Auctions.all_actions")}
            </Button>
          )}
        </Group>
        <Container size="xl" style={{ overflow: "visible" }} p={2} mx={0}>
          <Skeleton visible={isLoading}>
            <Carousel
              slideSize={{ base: "70%", sm: "45%", md: "23%" }}
              slides={slidesToRender.map((auction, index) => (
                <AuctionCard
                  key={index}
                  auction={auction}
                  index={index}
                  onClick={handleOnCardClick(auction?.id)}
                  to={getPath?.(auction?.id)}
                />
              ))}
              withIndicators={hasIndicators}
              pb={hasIndicators ? "xl" : 0}
            />
          </Skeleton>
        </Container>
      </Stack>
    </>
  );
};
